/**
 * PATH: apps/quote/src/components/builder/panels/inspector/AdvancedTab.tsx
 * FILE_ID: QB-INSPECTOR-ADVANCED-001
 * PURPOSE: Power-user view of a single line item — math chain, active formulas,
 *          custom-value cascade, and reset actions. The SAM-parity Advanced
 *          Drawer port estimators have been asking for.
 * WHY: Estimators need a single place to see "what is actually driving this
 *      row's price". Today that information is scattered across columns,
 *      config, and per-line overrides. This tab consolidates it.
 * FLOW:
 *   ┌──────────────────┐    ┌───────────────────┐    ┌────────────────────┐
 *   │ line item +      │ -> │ derived breakdown │ -> │ Pricing-mode badge,│
 *   │ quote.config     │    │ (computeLine,     │    │ math chain, active │
 *   │ (custom_columns) │    │  formula eval)    │    │ formulas + cascade │
 *   └──────────────────┘    └───────────────────┘    └────────────────────┘
 * DEPENDENCIES:
 *   - QB-MATH-COMPUTELINE-001: derived numerics
 *   - QB-MATH-FORMULA-001: re-evaluate formulas with current context
 * RELATED:
 *   - panels/RowInspectorPanel.tsx (mounts this tab)
 *   - sheets/CustomColumnsSection.tsx (declares the columns this tab references)
 */

import { useMemo } from 'react'
import {
  Sigma,
  Calculator,
  RefreshCcw,
  AlertTriangle,
  Sparkles,
  Trash2,
  ArrowRight,
} from 'lucide-react'
import { cn, formatCurrency } from '@/lib/utils'
import type { QuoteLineItem, QuoteDetail } from '@/lib/quoteApi'
import { computeLine } from '@/lib/quote-builder-math'
import { evaluateCell, type FormulaContext } from '@/lib/quote-builder-math/formula'
import type { CustomColumnDef } from '../../scope-sheet-grid/ScopeSheetGridTypes'

interface AdvancedTabProps {
  lineItem: QuoteLineItem
  quote: QuoteDetail | null
  onUpdate: (patch: Partial<QuoteLineItem>) => void
}

function readCustomColumns(quote: QuoteDetail | null): CustomColumnDef[] {
  if (!quote) return []
  const cfg = (quote.config ?? {}) as Record<string, unknown>
  const raw = cfg.custom_columns
  if (!Array.isArray(raw)) return []
  return raw.filter(
    (x): x is CustomColumnDef =>
      typeof x === 'object' && x !== null && typeof (x as CustomColumnDef).key === 'string',
  )
}

function buildFormulaContext(item: QuoteLineItem): FormulaContext {
  const N = (v: unknown): number => {
    const n = Number.parseFloat(String(v ?? 0))
    return Number.isFinite(n) ? n : 0
  }
  const qty = N(item.quantity)
  const pmpd = N(item.productivity_pmpd)
  const labour = N(item.labour_chargeout)
  const material = N(item.material_chargeout)
  const plant = N(item.plant_chargeout)
  const subcon = N(item.subcon_chargeout)
  const adjusted = N(item.adjusted_rate)
  return {
    quantity: qty, qty,
    productivity_pmpd: pmpd, pmpd,
    labour_chargeout: labour, labour,
    material_chargeout: material, material,
    plant_chargeout: plant, plant,
    subcon_chargeout: subcon, subcon,
    adjusted_rate: adjusted, rate: adjusted || (qty > 0 ? labour / qty : 0),
    labour_days: pmpd > 0 ? qty / pmpd : 0, days: pmpd > 0 ? qty / pmpd : 0,
    labour_hours: pmpd > 0 ? (qty / pmpd) * 8 : 0, hrs: pmpd > 0 ? (qty / pmpd) * 8 : 0,
    rate_per_unit: qty > 0 ? labour / qty : 0,
    chargeout_total: labour + material + plant + subcon,
  }
}

function StepCard({
  label,
  value,
  formula,
  unit,
  highlight,
}: {
  label: string
  value: string
  formula?: string
  unit?: string
  highlight?: 'primary' | 'cost' | 'chargeout' | 'margin'
}) {
  const tone =
    highlight === 'chargeout'
      ? 'border-emerald-300 bg-emerald-50 dark:bg-emerald-950/20 dark:border-emerald-800'
      : highlight === 'cost'
      ? 'border-blue-300 bg-blue-50 dark:bg-blue-950/20 dark:border-blue-800'
      : highlight === 'margin'
      ? 'border-amber-300 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-800'
      : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900'
  return (
    <div className={cn('rounded-md border px-3 py-2 flex items-center justify-between gap-2', tone)}>
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-wider text-slate-500 dark:text-slate-400 font-semibold">
          {label}
        </div>
        {formula && (
          <div className="text-[10px] font-mono text-slate-400 dark:text-slate-500 truncate">{formula}</div>
        )}
      </div>
      <div className="text-right">
        <div className="text-sm font-semibold text-slate-900 dark:text-white tabular-nums">{value}</div>
        {unit && <div className="text-[9px] text-slate-400 dark:text-slate-500">{unit}</div>}
      </div>
    </div>
  )
}

export default function AdvancedTab({ lineItem, quote, onUpdate }: AdvancedTabProps) {
  const liveInputs = useMemo(
    () => ({
      quantity: lineItem.quantity,
      productivity_pmpd: lineItem.productivity_pmpd,
      labour_chargeout: lineItem.labour_chargeout,
      material_chargeout: lineItem.material_chargeout,
      plant_chargeout: lineItem.plant_chargeout,
      subcon_chargeout: lineItem.subcon_chargeout,
      adjusted_rate: lineItem.adjusted_rate,
    }),
    [lineItem],
  )
  const live = useMemo(() => computeLine(liveInputs), [liveInputs])
  const formulaCtx = useMemo(() => buildFormulaContext(lineItem), [lineItem])

  // Evaluate every active formula so the panel can show both the expression
  // AND the current evaluated number side-by-side.
  const formulasMap = (lineItem.formulas ?? {}) as Record<string, string>
  const formulaEvaluations = useMemo(() => {
    const out: Array<{
      field: string
      expr: string
      ok: boolean
      value?: number
      error?: string
    }> = []
    for (const [field, expr] of Object.entries(formulasMap)) {
      if (typeof expr !== 'string' || !expr.trim().startsWith('=')) continue
      const r = evaluateCell(expr, formulaCtx)
      out.push({
        field,
        expr,
        ok: r.ok,
        value: r.ok ? r.value : undefined,
        error: r.ok ? undefined : `${r.error}`,
      })
    }
    return out
  }, [formulasMap, formulaCtx])

  // Custom-value cascade: union of (column defs) + (line overrides)
  const customColumns = useMemo(() => readCustomColumns(quote), [quote])
  const customMap = (lineItem.custom_values ?? {}) as Record<string, unknown>
  const customRows = useMemo(() => {
    const rows: Array<{ key: string; label: string; line: unknown; setup: unknown; source: 'override' | 'default' | 'unset' }> = []
    const seen = new Set<string>()
    for (const col of customColumns) {
      seen.add(col.key)
      const line = customMap[col.key]
      const hasLine = line !== undefined && line !== null && line !== ''
      const setup = col.default_value
      rows.push({
        key: col.key,
        label: col.label || col.key,
        line: hasLine ? line : undefined,
        setup,
        source: hasLine ? 'override' : (setup !== null && setup !== undefined ? 'default' : 'unset'),
      })
    }
    // Stale overrides (column was deleted but line still has a value) — surfaced
    // so estimators can clean them up.
    for (const k of Object.keys(customMap)) {
      if (seen.has(k)) continue
      rows.push({ key: k, label: `${k} (orphaned)`, line: customMap[k], setup: undefined, source: 'override' })
    }
    return rows
  }, [customColumns, customMap])

  // Pricing mode + override badges
  const pricingMode: 'manual' | 'calculated' =
    lineItem.pricing_mode === 'manual' || lineItem.pricing_mode === 'calculated'
      ? lineItem.pricing_mode
      : lineItem.override_chargeout != null
      ? 'manual'
      : 'calculated'
  const hasAdjustedRate =
    lineItem.adjusted_rate != null && Number.parseFloat(String(lineItem.adjusted_rate)) > 0

  // Bulk reset actions.
  //
  // NB: We deliberately only patch `adjusted_rate` here because that is the
  // ONE override field present in QuoteLineItemUpdateSerializer.Meta.fields
  // (see quotebuilder/serializers/line_item.py). `override_chargeout`,
  // `override_labour_cost`, and `override_material_cost` were sent by the
  // earlier version of this handler but were silently dropped by DRF — they
  // are not in the update fields list, so the request succeeded with no
  // schema error AND no effect, which is the worst possible failure mode.
  // `override_chargeout` is a derived field on the backend: it auto-clears
  // when component chargeouts are recalculated (manual_touched / computed_inputs_touched
  // branches in QuoteLineItemUpdateSerializer.update). To revert a row to
  // calculated pricing, the user should edit a driving input (PMPD, quantity)
  // in the grid — which the existing copy in OverridesTab now explains.
  const handleClearAllFormulas = () => {
    onUpdate({ formulas: {} })
  }
  const handleClearOverrides = () => {
    onUpdate({ adjusted_rate: null } as Partial<QuoteLineItem>)
  }
  const handleClearCustomValues = () => {
    onUpdate({ custom_values: {} })
  }

  return (
    <div className="p-3 space-y-4">
      {/* Pricing mode + override summary */}
      <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/50 p-3">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Calculator className="w-4 h-4 text-indigo-500" />
            <span className="text-sm font-semibold text-slate-900 dark:text-white">Pricing mode</span>
          </div>
          <span
            className={cn(
              'px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider',
              pricingMode === 'manual'
                ? 'bg-blue-600 text-white'
                : 'bg-emerald-600 text-white',
            )}
          >
            {pricingMode}
          </span>
        </div>
        <p className="text-xs text-slate-500 dark:text-slate-400 leading-snug">
          {pricingMode === 'calculated'
            ? 'Chargeout is derived from quantity × productivity × rates. Editing a component chargeout cell switches this row to manual.'
            : 'Chargeout is fixed (override). Editing a driving input (PMPD, adjusted rate) switches this row back to calculated.'}
        </p>
        {(hasAdjustedRate ||
          lineItem.override_chargeout != null ||
          lineItem.override_labour_cost != null ||
          lineItem.override_material_cost != null) && (
          <div className="mt-2 flex flex-wrap gap-1">
            {hasAdjustedRate && (
              <span className="px-1.5 py-0.5 rounded text-[10px] bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300">
                adj rate {formatCurrency(Number(lineItem.adjusted_rate) || 0)}
              </span>
            )}
            {lineItem.override_chargeout != null && (
              <span className="px-1.5 py-0.5 rounded text-[10px] bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
                override chargeout
              </span>
            )}
          </div>
        )}
      </div>

      {/* Math chain */}
      <div>
        <div className="flex items-center gap-2 mb-2">
          <ArrowRight className="w-4 h-4 text-indigo-500" />
          <span className="text-sm font-semibold text-slate-900 dark:text-white">Math chain</span>
        </div>
        <div className="space-y-1.5">
          <StepCard
            label="Quantity"
            value={`${Number(lineItem.quantity ?? 0).toFixed(2)}`}
            unit={String(lineItem.unit ?? 'm²')}
          />
          <StepCard
            label="Productivity (PMPD)"
            value={`${Number(lineItem.productivity_pmpd ?? 0).toFixed(2)}`}
            unit={`${lineItem.unit ?? 'm²'}/person/day`}
          />
          <StepCard
            label="Labour days"
            formula="qty / pmpd"
            value={live.labour_days.toFixed(3)}
            unit="d"
            highlight="cost"
          />
          <StepCard
            label="Labour hours"
            formula="qty / pmpd × 8"
            value={live.labour_hours.toFixed(2)}
            unit="hr"
            highlight="cost"
          />
          <StepCard
            label="Labour chargeout"
            value={formatCurrency(Number(lineItem.labour_chargeout ?? 0))}
            highlight="chargeout"
          />
          <StepCard
            label="Material chargeout"
            value={formatCurrency(Number(lineItem.material_chargeout ?? 0))}
            highlight="chargeout"
          />
          <StepCard
            label="Plant chargeout"
            value={formatCurrency(Number(lineItem.plant_chargeout ?? 0))}
            highlight="chargeout"
          />
          <StepCard
            label="Subcon chargeout"
            value={formatCurrency(Number(lineItem.subcon_chargeout ?? 0))}
            highlight="chargeout"
          />
          <StepCard
            label="Total chargeout"
            formula="labour + material + plant + subcon"
            value={formatCurrency(live.chargeout_total)}
            highlight="chargeout"
          />
          <StepCard
            label="Rate per unit"
            formula="labour_chargeout / qty"
            value={formatCurrency(live.rate_per_unit)}
          />
          <StepCard
            label="Margin %"
            value={`${(live.margin_percent || Number(lineItem.margin_percent ?? 0)).toFixed(1)}%`}
            highlight="margin"
          />
        </div>
      </div>

      {/* Active formulas */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Sigma className="w-4 h-4 text-violet-500" />
            <span className="text-sm font-semibold text-slate-900 dark:text-white">Active formulas</span>
            <span className="text-[10px] text-slate-400">({formulaEvaluations.length})</span>
          </div>
          {formulaEvaluations.length > 0 && (
            <button
              onClick={handleClearAllFormulas}
              className="flex items-center gap-1 px-2 py-0.5 text-[10px] text-slate-500 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
              title="Clear every formula on this row (numeric values stay)"
            >
              <Trash2 className="w-3 h-3" />
              Clear all
            </button>
          )}
        </div>
        {formulaEvaluations.length === 0 ? (
          <div className="text-xs text-slate-400 dark:text-slate-500 italic px-3 py-2 border border-dashed rounded">
            No formulas on this row. Type "=" into any numeric cell to start one.
          </div>
        ) : (
          <div className="space-y-1">
            {formulaEvaluations.map((f) => (
              <div
                key={f.field}
                className={cn(
                  'flex items-center justify-between gap-2 px-2 py-1.5 rounded border text-xs',
                  f.ok
                    ? 'bg-violet-50/40 border-violet-200 dark:bg-violet-950/20 dark:border-violet-800'
                    : 'bg-red-50/40 border-red-200 dark:bg-red-950/20 dark:border-red-800',
                )}
              >
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-600 dark:text-slate-300">
                    {f.field}
                  </div>
                  <div className="font-mono text-violet-700 dark:text-violet-300 truncate">{f.expr}</div>
                </div>
                <div className="text-right tabular-nums">
                  {f.ok ? (
                    <span className="font-semibold text-violet-900 dark:text-violet-200">
                      {Number.isFinite(f.value!) ? f.value!.toFixed(2) : '—'}
                    </span>
                  ) : (
                    <span className="font-semibold text-red-600 dark:text-red-300 flex items-center gap-1">
                      <AlertTriangle className="w-3 h-3" />
                      {f.error}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Custom-value cascade */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-rose-500" />
            <span className="text-sm font-semibold text-slate-900 dark:text-white">Custom values</span>
            <span className="text-[10px] text-slate-400">({customRows.length})</span>
          </div>
          {Object.keys(customMap).length > 0 && (
            <button
              onClick={handleClearCustomValues}
              className="flex items-center gap-1 px-2 py-0.5 text-[10px] text-slate-500 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
              title="Clear every custom override on this row (Setup defaults remain)"
            >
              <Trash2 className="w-3 h-3" />
              Clear overrides
            </button>
          )}
        </div>
        {customRows.length === 0 ? (
          <div className="text-xs text-slate-400 dark:text-slate-500 italic px-3 py-2 border border-dashed rounded">
            No custom columns declared. Use the Setup tab → Custom Columns to add some.
          </div>
        ) : (
          <div className="space-y-1">
            {customRows.map((r) => (
              <div
                key={r.key}
                className="grid grid-cols-[1fr_auto_auto] items-center gap-2 px-2 py-1.5 rounded border border-rose-100 dark:border-rose-900/40 bg-rose-50/40 dark:bg-rose-950/20 text-xs"
              >
                <div className="min-w-0">
                  <div className="text-slate-700 dark:text-slate-200 font-medium truncate">{r.label}</div>
                  <div className="text-[10px] font-mono text-slate-400 dark:text-slate-500">{r.key}</div>
                </div>
                <div className="text-right">
                  {r.source === 'override' && (
                    <span className="px-1.5 py-0.5 rounded text-[9px] font-semibold bg-rose-600 text-white">
                      override
                    </span>
                  )}
                  {r.source === 'default' && (
                    <span className="px-1.5 py-0.5 rounded text-[9px] font-semibold bg-slate-200 text-slate-600 dark:bg-slate-700 dark:text-slate-300">
                      Setup default
                    </span>
                  )}
                  {r.source === 'unset' && (
                    <span className="px-1.5 py-0.5 rounded text-[9px] font-semibold bg-slate-100 text-slate-400 dark:bg-slate-800 dark:text-slate-500">
                      unset
                    </span>
                  )}
                </div>
                <div className="text-right tabular-nums font-medium text-slate-900 dark:text-white">
                  {r.source === 'override'
                    ? String(r.line ?? '—')
                    : r.source === 'default'
                    ? String(r.setup ?? '—')
                    : '—'}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Reset actions */}
      <div className="border-t border-slate-200 dark:border-slate-800 pt-3">
        <div className="flex items-center gap-2 mb-2">
          <RefreshCcw className="w-4 h-4 text-slate-500" />
          <span className="text-sm font-semibold text-slate-900 dark:text-white">Reset</span>
        </div>
        <div className="grid grid-cols-3 gap-2">
          <button
            onClick={handleClearOverrides}
            className="px-2 py-1.5 text-[11px] font-medium rounded border border-slate-200 dark:border-slate-700 hover:bg-yellow-50 hover:border-yellow-300 dark:hover:bg-yellow-900/20 text-slate-700 dark:text-slate-200 transition-colors"
            title="Clear the per-line adjusted rate. To revert a row out of manual chargeout mode, edit a driving input (quantity, PMPD) in the grid."
          >
            Clear adjusted rate
          </button>
          <button
            onClick={handleClearAllFormulas}
            className="px-2 py-1.5 text-[11px] font-medium rounded border border-slate-200 dark:border-slate-700 hover:bg-violet-50 hover:border-violet-300 dark:hover:bg-violet-900/20 text-slate-700 dark:text-slate-200 transition-colors"
            title="Clear every persisted formula on this row (numeric cell values are preserved)"
          >
            Clear formulas
          </button>
          <button
            onClick={handleClearCustomValues}
            className="px-2 py-1.5 text-[11px] font-medium rounded border border-slate-200 dark:border-slate-700 hover:bg-rose-50 hover:border-rose-300 dark:hover:bg-rose-900/20 text-slate-700 dark:text-slate-200 transition-colors"
            title="Clear every custom-column override — row falls back to Setup defaults"
          >
            Clear custom values
          </button>
        </div>
      </div>
    </div>
  )
}
